import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaBook,
  FaSpinner,
  FaCheckCircle,
  FaSave,
  FaCopy,
  FaListOl, // ✅ ADD THIS IMPORT
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type TaskAnswer = {
  taskId: number;
  taskType: string;
  taskDescription: string;
  studentAnswer: string;
  isCorrect: boolean | null;
  scoreEarned: number | null;
  aiFeedback: string | null;
};

type Submission = {
  submissionId: number;
  homeworkId: number;
  homeworkTitle: string;
  totalScore: number | null;
  aiFeedback: string;
  submittedAt: string;
  evaluated: boolean;
  taskAnswers: TaskAnswer[];
};

const StudentHomeworkSubmissionPage: React.FC = () => {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSaveWarning, setShowSaveWarning] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check if submission data was passed from complete page
    const stateData = (location.state as any)?.submissionData;
    if (stateData) {
      setSubmission(stateData);
      setLoading(false);
    } else {
      fetchSubmission();
    }
  }, [homeworkId]);

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/homework/${homeworkId}/submission`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setSubmission(data);
      } else {
        setError("Failed to load submission");
      }
    } catch (err) {
      console.error("Error fetching submission:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ ADD THIS HELPER FUNCTION
  const getQuestionCount = (answer: TaskAnswer): number => {
    const taskType = answer.taskType;
    const studentAnswer = answer.studentAnswer || "";

    // For HÖREN and ERGÄNZUNGEN, count answers
    if (taskType === "HOREN" || taskType === "ERGANZUNGEN") {
      if (!studentAnswer || studentAnswer.trim() === "") {
        return 1;
      }

      try {
        // Try parsing as JSON array
        if (studentAnswer.trim().startsWith("[")) {
          const parsed = JSON.parse(studentAnswer);
          return parsed.length > 0 ? parsed.length : 1;
        }
      } catch (e) {
        // Not JSON
      }

      // Fallback: Count lines
      const lines = studentAnswer
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line !== "");

      return lines.length > 0 ? lines.length : 1;
    }

    return 1;
  };

  const handleCopyFeedback = () => {
    if (!submission) return;

    let textToCopy = `HOMEWORK FEEDBACK\n`;
    textToCopy += `===================\n\n`;
    textToCopy += `Title: ${submission.homeworkTitle}\n`;
    textToCopy += `Total Score: ${submission.totalScore}/20\n`;
    textToCopy += `Submitted: ${new Date(
      submission.submittedAt
    ).toLocaleString()}\n\n`;
    textToCopy += `===================\n\n`;
    textToCopy += `AI FEEDBACK:\n\n${submission.aiFeedback}\n\n`;
    textToCopy += `===================\n\n`;
    textToCopy += `YOUR ANSWERS:\n\n`;

    submission.taskAnswers.forEach((answer, index) => {
      textToCopy += `Task ${index + 1}:\n`;
      textToCopy += `Question: ${answer.taskDescription}\n`;
      textToCopy += `Your Answer: ${answer.studentAnswer}\n`;
      if (answer.scoreEarned !== null) {
        textToCopy += `Score Earned: ${answer.scoreEarned}\n`;
      }
      textToCopy += `\n`;
    });

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            {error || "Submission not found"}
          </p>
          <button
            onClick={() => navigate("/student/my-homework")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Back to Homework
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="Homework Feedback"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Homework", to: "/student/my-homework" },
          { label: "Feedback" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Success Card */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FaCheckCircle className="text-green-600 text-3xl" />
            <div>
              <h2 className="text-2xl font-bold text-green-800">
                Homework Submitted Successfully!
              </h2>
              <p className="text-sm text-green-700 mt-1">
                Your homework has been evaluated
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-green-200">
            <div>
              <p className="text-sm text-gray-600 mb-1">Your Score:</p>
              <p className="text-4xl font-bold text-[#009fe3]">
                {submission.totalScore !== null
                  ? submission.totalScore.toFixed(1)
                  : "N/A"}
                <span className="text-2xl text-gray-600">/20</span>
              </p>
            </div>
            <button
              onClick={handleCopyFeedback}
              className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-md"
            >
              {copied ? (
                <>
                  <FaCheckCircle />
                  Copied!
                </>
              ) : (
                <>
                  <FaCopy />
                  Copy All Feedback
                </>
              )}
            </button>
          </div>
        </div>

        {/* AI Feedback */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaCheckCircle className="text-green-600" />
            Feedback & Evaluation
          </h2>

          <div className="prose prose-sm max-w-none">
            <div
              className="text-sm text-gray-800 leading-relaxed"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {submission.aiFeedback
                .split("\n")
                .map((line: string, index: number) => {
                  if (line.includes("**")) {
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                      <p key={index} className="mb-2">
                        {parts.map((part: string, i: number) => {
                          if (part.startsWith("**") && part.endsWith("**")) {
                            return (
                              <strong key={i} className="text-gray-900">
                                {part.slice(2, -2)}
                              </strong>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </p>
                    );
                  }
                  if (line.trim()) {
                    return (
                      <p key={index} className="mb-2">
                        {line}
                      </p>
                    );
                  }
                  return <br key={index} />;
                })}
            </div>
          </div>
        </div>

        {/* Your Answers */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Your Submitted Answers
          </h2>

          <div className="space-y-4">
            {submission.taskAnswers.map((answer, index) => {
              // ✅ CALCULATE question count and task type for each answer
              const questionCount = getQuestionCount(answer);
              const isMultipleAnswerTask =
                answer.taskType === "HOREN" ||
                answer.taskType === "ERGANZUNGEN";

              return (
                <div
                  key={answer.taskId}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">
                      Task {index + 1}
                    </h3>
                  </div>

                  {/* ✅ ADD TASK TYPE BADGE HERE */}
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold">
                      {answer.taskType}
                    </span>

                    {isMultipleAnswerTask && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">
                        <FaListOl className="text-xs" />
                        {questionCount}{" "}
                        {questionCount === 1 ? "question" : "questions"}
                      </span>
                    )}
                  </div>

                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Question:
                    </h4>
                    <p className="text-sm text-gray-800 bg-white p-3 rounded border border-gray-200">
                      {answer.taskDescription}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Your Answer:
                    </h4>
                    <div className="text-sm text-gray-800 bg-white p-3 rounded border border-gray-200">
                      {/* ✅ FORMAT MULTIPLE ANSWERS PROPERLY */}
                      {isMultipleAnswerTask ? (
                        <div className="space-y-2">
                          {(() => {
                            try {
                              // Try parsing as JSON array
                              if (answer.studentAnswer.trim().startsWith("[")) {
                                const parsed = JSON.parse(answer.studentAnswer);
                                return parsed.map(
                                  (ans: string, idx: number) => (
                                    <div
                                      key={idx}
                                      className="flex items-start gap-2"
                                    >
                                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                                        {idx + 1}
                                      </span>
                                      <span className="flex-1">
                                        {ans || "(empty)"}
                                      </span>
                                    </div>
                                  )
                                );
                              }
                            } catch (e) {
                              // Not JSON, fall through
                            }

                            // Fallback: Show as plain text with line breaks
                            return (
                              <pre className="whitespace-pre-wrap font-sans">
                                {answer.studentAnswer}
                              </pre>
                            );
                          })()}
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">
                          {answer.studentAnswer}
                        </p>
                      )}
                    </div>
                  </div>

                  {answer.isCorrect !== null && (
                    <div className="mt-3">
                      {answer.isCorrect ? (
                        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-3 py-1 rounded-full text-xs font-semibold border border-green-200">
                          <FaCheckCircle />
                          Correct
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-3 py-1 rounded-full text-xs font-semibold border border-red-200">
                          ✗ Incorrect
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCopyFeedback}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-md"
          >
            {copied ? (
              <>
                <FaCheckCircle />
                Copied!
              </>
            ) : (
              <>
                <FaCopy />
                Copy All Feedback
              </>
            )}
          </button>
          <button
            onClick={() => navigate("/student/my-homework")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Back to Homework List
          </button>
        </div>
      </main>
    </div>
  );
};

export default StudentHomeworkSubmissionPage;
